import { useCallback, useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DeleteFilled, EyeOutlined } from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import SiteCategoriesAPI from 'libs/api/admin/siteCategories';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import { useFilter } from 'hooks/useFilter';

const api = new SiteCategoriesAPI();

const useSiteCategory = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const pages = useRecoilValue(getPage);
  const { filterData } = useFilter();
  const companyCategories = useMemo(
    () => filterData?.company_categories || [],
    [filterData?.company_categories],
  );
  const {
    data,
    singleItem: singleData,
    loading,
    onChangeTableFilter,
    onDelete: handleDeleteCategory,
    updateInProcess,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'site_categories',
    singleResourceKey: 'site_category',
    api,
    isMainPage,
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo (Italiano)'),
        type: 'input',
        name: 'by_title_it',
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('ID'),
        dataIndex: 'id',
        width: 80,
        sorter: () => {},
      },
      {
        title: t('Categorie Sito'),
        dataIndex: 'title_it',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Categorie insegne'),
        dataIndex: 'company_category',
        width: 120,
        sorter: () => {},
        render: (data, row) => {
          const categoryId = row?.company_category_id;
          const categoryFromFilters = companyCategories.find(
            (item) => String(item.id || item.value) === String(categoryId),
          );
          return (
            categoryFromFilters?.title ||
            row?.company_category?.title ||
            categoryId ||
            ''
          );
        },
      },
      {
        title: '',
        dataIndex: 'actions',
        align: 'right',
        width: 160,
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row gutter={16} justify={'end'}>
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteFilled />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [t, pathname, pages?.ability?.can_read, companyCategories]);

  const handleCreateOrUpdate = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      try {
        const data = {
          site_category: {
            ...values,
          },
        };
        if (!id) {
          await api.create({ data });
          navigate('/admin/site_categories');
        } else {
          await api.put(id, { data });
          navigate(`/admin/site_categories/${id}?success=true`);
        }
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, id],
  );

  useEffect(() => {
    if (id && !isMainPage && singleData) {
      try {
        let fieldsValues = [];
        for (let key in singleData) {
          if (key !== 'id' && key !== 'can_destroy' && key !== 'can_update') {
            fieldsValues.push({
              name: key,
              value: singleData[key],
            });
          }
        }
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, isMainPage, singleData, form]);

  return {
    columns,
    data,
    singleData,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    companyCategories,
    handleCreateOrUpdate,
    handleDeleteCategory,
  };
};

export default useSiteCategory;
